import React, { useRef } from "react";
import "./SidebarIcon.css";
import SidebarIconSubmenu from "./SidebarIconSubmenu";
import { Link } from "react-router-dom";

// Visual gap between the icon column and the popout menu.
const SUBMENU_GAP = 8;

function SidebarIcon({ icon, submenu, to, onClick, disabled = false, badge }) {
  const isActive = window.location.pathname.includes(to);
  const iconRef = useRef(null);
  const submenuRef = useRef(null);
  const bridgeRef = useRef(null);

  // Submenu is position:fixed so the sidebar's overflow-y:auto doesn't clip
  // it. Compute its top/left from the icon's rect on every hover, and clamp
  // so the bottom of the menu never falls off-screen — important for the
  // items near the bottom of the sidebar (Personalizar menú, Salir).
  const positionSubmenu = () => {
    if (!submenu || !iconRef.current || !submenuRef.current) return;
    const iconRect = iconRef.current.getBoundingClientRect();
    const node = submenuRef.current;
    const submenuHeight = node.offsetHeight;
    const margin = 8;
    let top = iconRect.top - 16; // slight overlap above the icon, matching prior look
    const maxTop = window.innerHeight - submenuHeight - margin;
    if (top > maxTop) top = maxTop;
    if (top < margin) top = margin;
    node.style.top = `${top}px`;
    node.style.left = `${iconRect.right + SUBMENU_GAP}px`;

    // Hover bridge: the menu only stays open while the pointer is inside this
    // wrapper (CSS :hover on the DOM subtree), so the SUBMENU_GAP px of dead
    // space between icon and menu closes it if crossed slowly. This invisible
    // strip spans that gap and is a child of the wrapper, keeping :hover alive.
    // It must live OUTSIDE the submenu: the submenu's overflow-y:auto forces
    // overflow-x to auto too (per spec, visible + auto => auto), which clips
    // any bridge positioned at a negative left inside it.
    // Spanning the union of icon + menu vertically also covers diagonal moves.
    const bridge = bridgeRef.current;
    if (bridge) {
      const bTop = Math.min(iconRect.top, top);
      const bBottom = Math.max(iconRect.bottom, top + submenuHeight);
      bridge.style.top = `${bTop}px`;
      bridge.style.height = `${bBottom - bTop}px`;
      bridge.style.left = `${iconRect.right}px`;
      // +2 to absorb sub-pixel rounding so there is no 1px dead seam.
      bridge.style.width = `${SUBMENU_GAP + 2}px`;
    }
  };

  return (
    <div
      className={`sidebar_icon_whole_wrapper ${!disabled || "disabled"}`}
      onClick={onClick}
      onMouseEnter={positionSubmenu}
      ref={iconRef}
    >
      <Link to={to}>
        <div
          className={`main_sidebar_icon_wrapper ${isActive ? "active" : ""}`}
        >
          <span className="sidebar_icon_image">{icon}</span>
          {badge > 0 && <span className="sidebar_icon_badge">{badge}</span>}
        </div>
      </Link>
      {submenu ? (
        <>
          <div className="sidebar_hover_bridge" ref={bridgeRef} aria-hidden="true" />
          <SidebarIconSubmenu source={submenu} ref={submenuRef} />
        </>
      ) : (
        <></>
      )}
    </div>
  );
}

export default SidebarIcon;
